import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

const PatientFavorites = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    fetchFavorites();
  }, [user, token]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/patients/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch favorites');
      }

      const data = await response.json();
      setFavorites(data.favorites || []);
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFavorite = async (doctorId) => {
    try {
      setRemovingId(doctorId);
      const response = await fetch(`${API_URL}/patients/favorites/${doctorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove from favorites');
      }

      // Remove from local state immediately for instant UI feedback
      setFavorites(prev => prev.filter(fav => fav.doctorId !== doctorId));
    } catch (err) {
      console.error('Error removing favorite:', err);
      setError(err.message);
    } finally {
      setRemovingId(null);
    }
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <svg
            key={i}
            className={`w-4 h-4 ${
              i < fullStars
                ? 'text-yellow-400'
                : i === fullStars && hasHalfStar
                ? 'text-yellow-400'
                : 'text-gray-600'
            }`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
        <span className="ml-1 text-light-text-secondary dark:text-silver/60 text-sm">
          {rating > 0 ? rating.toFixed(1) : 'No ratings'}
        </span>
      </div>
    );
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen pb-16">
      {/* Header */}
      <section className="pt-8 pb-8 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-4xl italic text-light-text dark:text-pure-white mb-2">My Favorite Doctors</h1>
          <p className="text-light-text-secondary dark:text-silver/60">Doctors you've saved for quick access</p>
        </div>
      </section>

      {/* Favorites List */}
      <section className="px-6">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
            </div>
          ) : error ? (
            <div className="card text-center py-8">
              <p className="text-red-400">{error}</p>
              <button
                onClick={fetchFavorites}
                className="mt-4 btn-primary"
              >
                Try Again
              </button>
            </div>
          ) : favorites.length === 0 ? (
            <div className="card text-center py-12">
              <svg className="w-16 h-16 text-slate-300 dark:text-silver/30 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <h3 className="font-display text-xl italic text-light-text dark:text-pure-white mb-2">No Favorites Yet</h3>
              <p className="text-light-text-secondary dark:text-silver/60 mb-6">You haven't saved any doctors to your favorites.</p>
              <Link to="/marketplace" className="btn-primary inline-block">
                Find Doctors
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {favorites.map((favorite) => (
                <div key={favorite.favoriteId} className="card" data-testid="favorite-card">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Doctor Photo */}
                    <div className="w-16 h-16 rounded-xl bg-silver/10 flex-shrink-0 overflow-hidden">
                      {favorite.photoUrl ? (
                        <img
                          src={favorite.photoUrl}
                          alt={`Dr. ${favorite.firstName} ${favorite.lastName}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-slate-400 dark:text-silver/40" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>

                    {/* Doctor Info */}
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-medium text-light-text dark:text-pure-white text-lg truncate"
                        title={`Dr. ${favorite.firstName} ${favorite.lastName}`}
                      >
                        Dr. {favorite.firstName} {favorite.lastName}
                      </h3>
                      {favorite.specialties && (Array.isArray(favorite.specialties) ? favorite.specialties : JSON.parse(favorite.specialties || '[]')).length > 0 && (
                        <p className="text-light-text-secondary dark:text-silver/60 text-sm mb-1">
                          {(Array.isArray(favorite.specialties) ? favorite.specialties : JSON.parse(favorite.specialties || '[]')).join(', ')}
                        </p>
                      )}
                      <div className="flex items-center gap-4">
                        {renderStars(favorite.averageRating)}
                        <span className="text-slate-400 dark:text-silver/40 text-sm">
                          ({favorite.reviewCount} review{favorite.reviewCount !== 1 ? 's' : ''})
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                      <Link
                        to={`/doctor/${favorite.doctorId}`}
                        className="btn-primary text-sm"
                      >
                        View Profile
                      </Link>
                      <button
                        onClick={() => handleRemoveFavorite(favorite.doctorId)}
                        disabled={removingId === favorite.doctorId}
                        className="p-2 text-slate-500 dark:text-silver/60 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-smooth disabled:opacity-50"
                        title="Remove from favorites"
                        data-testid="remove-favorite-button"
                      >
                        {removingId === favorite.doctorId ? (
                          <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                            <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PatientFavorites;
